import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { getFirebaseStorage } from "../../lib/firebase";
import EditorJsRenderer from "../EditorField/EditorJsRenderer";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "/api";

const CareerSection = ({ jobId }) => {
  const { t } = useTranslation();
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [formValues, setFormValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    earliestStartDate: "",
    germanLanguageLevel: "",
    gdprConsent: false,
    workAuthorization: false,
    message: "",
    resume: null,
  });
  const [formStatus, setFormStatus] = useState({
    loading: false,
    error: "",
    success: "",
  });
  const [emailCheck, setEmailCheck] = useState({
    loading: false,
    exists: false,
    error: "",
    lastChecked: "",
  });
  const [openDropdown, setOpenDropdown] = useState(null);
  const languageSelectRef = useRef(null);
  const workAuthSelectRef = useRef(null);
  const resumeInputRef = useRef(null);
  const [jobOpening, setJobOpening] = useState(null);
  const [jobOpeningStatus, setJobOpeningStatus] = useState({
    loading: true,
    error: "",
  });

  useEffect(() => {
    let ignore = false;

    if (!jobId) {
      setJobOpeningStatus({ loading: false, error: "Job opening not found." });
      return () => {
        ignore = true;
      };
    }

    const loadJobOpening = async () => {
      setJobOpeningStatus({ loading: true, error: "" });
      try {
        const response = await fetch(`${API_BASE}/job-openings/${jobId}`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.message || "Unable to load job opening.");
        }
        if (!ignore) {
          setJobOpening(data || null);
          setJobOpeningStatus({ loading: false, error: "" });
        }
      } catch (error) {
        if (!ignore) {
          setJobOpeningStatus({
            loading: false,
            error: error.message || "Unable to load job opening.",
          });
        }
      }
    };

    loadJobOpening();

    return () => {
      ignore = true;
    };
  }, [jobId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const languageNode = languageSelectRef.current;
      const workAuthNode = workAuthSelectRef.current;
      if (
        (languageNode && languageNode.contains(event.target)) ||
        (workAuthNode && workAuthNode.contains(event.target))
      ) {
        return;
      }
      setOpenDropdown(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const normalizeEmploymentTypes = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string" && value.trim()) return [value.trim()];
    return [];
  };

  const formatEmploymentType = (value) =>
    normalizeEmploymentTypes(value)
      .map((item) =>
        item
          .toLowerCase()
          .split("_")
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(" ")
      )
      .join(", ");

  const resolveLabel = (key, fallback) => t(key, { defaultValue: fallback }) || fallback;

  const todayIsoDate = new Date().toISOString().split("T")[0];
  const languageOptions = ["A1", "A2", "B1", "B2", "C1", "C2"];
  const allowedResumeTypes = new Set([
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ]);
  const maxResumeSizeBytes = 5 * 1024 * 1024;

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((current) => ({ ...current, [name]: value }));
    if (name === "email") {
      setEmailCheck((current) => ({
        ...current,
        exists: false,
        error: "",
        lastChecked: "",
      }));
    }
  };

  const checkEmailExists = async (emailValue, options = {}) => {
    const trimmed = String(emailValue || "").trim();
    if (!trimmed) {
      setEmailCheck({ loading: false, exists: false, error: "", lastChecked: "" });
      return { exists: false, error: false };
    }

    if (emailCheck.lastChecked === trimmed && !emailCheck.error) {
      return { exists: emailCheck.exists, error: false };
    }

    setEmailCheck((current) => ({ ...current, loading: true, error: "" }));

    try {
      const response = await fetch(
        `${API_BASE}/applications/check-email?email=${encodeURIComponent(trimmed)}`
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || "Unable to check email.");
      }

      const exists = Boolean(data.exists);
      setEmailCheck({ loading: false, exists, error: "", lastChecked: trimmed });
      return { exists, error: false };
    } catch (error) {
      if (options.silent) {
        setEmailCheck({ loading: false, exists: false, error: "", lastChecked: trimmed });
      } else {
        setEmailCheck({
          loading: false,
          exists: false,
          error: error.message || "Unable to check email.",
          lastChecked: trimmed,
        });
      }
      return { exists: false, error: true };
    }
  };

  const handleEmailBlur = () => {
    if (!formValues.email.trim()) return;
    checkEmailExists(formValues.email);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      setFormValues((current) => ({ ...current, resume: null }));
      return;
    }
    if (!allowedResumeTypes.has(file.type)) {
      setFormStatus({
        loading: false,
        error: resolveLabel("career_apply_error_file_type", "Unsupported file type."),
        success: "",
      });
      event.target.value = "";
      setFormValues((current) => ({ ...current, resume: null }));
      return;
    }
    if (file.size > maxResumeSizeBytes) {
      setFormStatus({
        loading: false,
        error: resolveLabel("career_apply_error_file_size", "File must be 5 MB or smaller."),
        success: "",
      });
      event.target.value = "";
      setFormValues((current) => ({ ...current, resume: null }));
      return;
    }
    setFormValues((current) => ({ ...current, resume: file }));
  };

  const uploadResume = async (file) => {
    const storage = getFirebaseStorage();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const storagePath = `/hausarzt-bharwani/candidate-resume/${uniqueSuffix}-${safeName}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file, { contentType: file.type });
    const downloadUrl = await getDownloadURL(storageRef);

    return { downloadUrl, storagePath };
  };


  const validateForm = () => {
    if (
      !formValues.firstName.trim() ||
      !formValues.lastName.trim() ||
      !formValues.email.trim() ||
      !formValues.phone.trim() ||
      !formValues.address.trim() ||
      !formValues.earliestStartDate ||
      !formValues.germanLanguageLevel ||
      !formValues.gdprConsent ||
      !formValues.resume
    ) {
      return t("career_apply_error_required");
    }
    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (formStatus.loading) {
      return;
    }

    if (!event.currentTarget.reportValidity()) {
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      setFormStatus({ loading: false, error: validationError, success: "" });
      return;
    }

    try {
      const emailResult = await checkEmailExists(formValues.email, { silent: true });
      if (emailResult.exists) {
        setFormStatus({ loading: false, error: "", success: "" });
        return;
      }
      if (emailResult.error) {
        setFormStatus({ loading: false, error: t("career_apply_error_submit"), success: "" });
        return;
      }
      setFormStatus({ loading: true, error: "", success: "" });
      const uploadResult = await uploadResume(formValues.resume);
      const payload = {
        firstName: formValues.firstName.trim(),
        lastName: formValues.lastName.trim(),
        email: formValues.email.trim(),
        phone: formValues.phone.trim(),
        address: formValues.address.trim(),
        earliestStartDate: formValues.earliestStartDate,
        germanLanguageLevels: formValues.germanLanguageLevel,
        gdprConsent: formValues.gdprConsent,
        workAuthorization: formValues.workAuthorization,
        message: formValues.message.trim(),
        resumeUrl: uploadResult.downloadUrl,
        resumeOriginalName: formValues.resume.name,
        resumeMimeType: formValues.resume.type,
        resumeStoragePath: uploadResult.storagePath,
        ...(jobId ? { jobOpeningId: jobId } : {}),
      };
      const response = await fetch(`${API_BASE}/applications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || t("career_apply_error_submit"));
      }

      setFormStatus({
        loading: false,
        error: "",
        success: t("career_apply_success"),
      });
      setFormValues({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        earliestStartDate: "",
        germanLanguageLevel: "",
        gdprConsent: false,
        workAuthorization: false,
        message: "",
        resume: null,
      });
      setEmailCheck({ loading: false, exists: false, error: "", lastChecked: "" });
      if (resumeInputRef.current) {
        resumeInputRef.current.value = "";
      }
    } catch (error) {
      setFormStatus({
        loading: false,
        error: error.message || t("career_apply_error_submit"),
        success: "",
      });
    }
  };

  return (
    <section id="career" className="section-padding career-section">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-10 text-center">
            <div className="wpo-section-title">
              <h2 style={{ fontWeight: "bold" }}>{t("career_title")}</h2>
            </div>
          </div>
        </div>
        <div className="career-back-link">
          <Link className="career-back-button" to="/career">
            {resolveLabel("career_back", "Back to all openings")}
          </Link>
        </div>
        <div className="row career-openings-grid">
          {jobOpeningStatus.loading && (
            <div className="col-12 text-center mb-4">
              <p>{resolveLabel("career_loading", "Loading job opening...")}</p>
            </div>
          )}
          {jobOpeningStatus.error && (
            <div className="col-12 mb-4">
              <div className="alert alert-danger" role="alert">
                {jobOpeningStatus.error}
              </div>
            </div>
          )}
          {!jobOpeningStatus.loading &&
            !jobOpeningStatus.error &&
            !jobOpening && (
              <div className="col-12 text-center mb-4">
                <p>{resolveLabel("career_no_openings", "Job opening not found.")}</p>
              </div>
            )}
          {!jobOpeningStatus.loading && !jobOpeningStatus.error && jobOpening && (
            <div className="col-12 mb-4">
              <div className="career-opening-card p-4 h-100">
                <div className="career-opening-header">
                  <div>
                    <h3 className="career-opening-title">{jobOpening.title}</h3>
                    {jobOpening.location && (
                      <p className="career-opening-meta">{jobOpening.location}</p>
                    )}
                  </div>
                  {jobOpening.employmentType && (
                    <span className="badge bg-primary career-opening-badge">
                      {formatEmploymentType(jobOpening.employmentType)}
                    </span>
                  )}
                </div>
                {jobOpening.description && (
                  <EditorJsRenderer value={jobOpening.description} className="career-opening-description" />
                )}
                <div className="row career-opening-sections">
                  {jobOpening.responsibilities && (
                    <div className="col-md-4 mb-3">
                      <h6>
                        {resolveLabel("career_responsibilities", "Responsibilities")}
                      </h6>
                      <EditorJsRenderer value={jobOpening.responsibilities} />
                    </div>
                  )}
                  {jobOpening.profile && (
                    <div className="col-md-4 mb-3">
                      <h6>{resolveLabel("career_profile", "Profile")}</h6>
                      <EditorJsRenderer value={jobOpening.profile} />
                    </div>
                  )}
                  {jobOpening.benefits && (
                    <div className="col-md-4 mb-3">
                      <h6>{resolveLabel("career_benefits", "Benefits")}</h6>
                      <EditorJsRenderer value={jobOpening.benefits} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="row justify-content-center">
          <div className="col-lg-10 text-center">
            <h3>{t("career_closing_title")}</h3>
            {!showApplyForm && (
              <div className="mt-4">
                <button
                  type="button"
                  className="theme-btn"
                  onClick={() => setShowApplyForm(true)}
                >
                  {t("career_apply_button")}
                </button>
              </div>
            )}
          </div>
        </div>
        {showApplyForm && (
          <div className="row justify-content-center mt-5">
            <div className="col-lg-8">
              <form onSubmit={handleSubmit} className="career-apply-form">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="career-first-name">
                      {t("career_form_first_name")}
                    </label>
                    <input
                      id="career-first-name"
                      name="firstName"
                      type="text"
                      className="form-control"
                      value={formValues.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="career-last-name">
                      {t("career_form_last_name")}
                    </label>
                    <input
                      id="career-last-name"
                      name="lastName"
                      type="text"
                      className="form-control"
                      value={formValues.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="career-email">
                      {t("career_form_email")}
                    </label>
                    <input
                      id="career-email"
                      name="email"
                      type="email"
                      className="form-control"
                      value={formValues.email}
                      onChange={handleInputChange}
                      onBlur={handleEmailBlur}
                      required
                    />
                    {emailCheck.exists && (
                      <div className="form-text text-danger">
                        {t("career_form_email_exists")}
                      </div>
                    )}
                    {emailCheck.error && !emailCheck.exists && (
                      <div className="form-text text-danger">
                        {emailCheck.error}
                      </div>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="career-phone">
                      {t("career_form_phone")}
                    </label>
                    <input
                      id="career-phone"
                      name="phone"
                      type="tel"
                      className="form-control"
                      value={formValues.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label className="form-label" htmlFor="career-address">
                      {t("career_form_address")}
                    </label>
                    <input
                      id="career-address"
                      name="address"
                      type="text"
                      className="form-control"
                      value={formValues.address}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="career-start-date">
                      {t("career_form_earliest_start")}
                    </label>
                    <input
                      id="career-start-date"
                      name="earliestStartDate"
                      type="date"
                      className="form-control"
                      value={formValues.earliestStartDate}
                      onChange={handleInputChange}
                      min={todayIsoDate}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      {t("career_form_german_level")}
                    </label>
                    <div className="career-custom-select" ref={languageSelectRef}>
                      <button
                        type="button"
                        className={`career-custom-select-trigger${
                          !formValues.germanLanguageLevel ? " is-placeholder" : ""
                        }`}
                        onClick={() =>
                          setOpenDropdown((current) =>
                            current === "language" ? null : "language"
                          )
                        }
                      >
                        {formValues.germanLanguageLevel ||
                          t("career_form_german_level_select")}
                      </button>
                      {openDropdown === "language" && (
                        <div className="career-custom-select-menu" role="listbox">
                          {languageOptions.map((level) => (
                            <button
                              type="button"
                              key={level}
                              className={`career-custom-select-option${
                                formValues.germanLanguageLevel === level
                                  ? " is-selected"
                                  : ""
                              }`}
                              onClick={() => {
                                setFormValues((current) => ({
                                  ...current,
                                  germanLanguageLevel: level,
                                }));
                                setOpenDropdown(null);
                              }}
                            >
                              {level}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="career-work-authorization">
                      {t("career_form_work_authorization")}
                    </label>
                    <div className="career-custom-select" ref={workAuthSelectRef}>
                      <button
                        type="button"
                        id="career-work-authorization"
                        className="career-custom-select-trigger"
                        onClick={() =>
                          setOpenDropdown((current) =>
                            current === "workAuth" ? null : "workAuth"
                          )
                        }
                      >
                        {formValues.workAuthorization
                          ? t("career_form_yes")
                          : t("career_form_no")}
                      </button>
                      {openDropdown === "workAuth" && (
                        <div className="career-custom-select-menu" role="listbox">
                          {[true, false].map((value) => (
                            <button
                              type="button"
                              key={value ? "yes" : "no"}
                              className={`career-custom-select-option${
                                formValues.workAuthorization === value
                                  ? " is-selected"
                                  : ""
                              }`}
                              onClick={() => {
                                setFormValues((current) => ({
                                  ...current,
                                  workAuthorization: value,
                                }));
                                setOpenDropdown(null);
                              }}
                            >
                              {value
                                ? t("career_form_yes")
                                : t("career_form_no")}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="career-message">
                    {t("career_form_message")}
                  </label>
                  <textarea
                    id="career-message"
                    name="message"
                    rows="4"
                    className="form-control"
                    value={formValues.message}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="career-resume">
                    {t("career_form_resume")}
                  </label>
                  <input
                    id="career-resume"
                    name="resume"
                    type="file"
                    className="form-control"
                    ref={resumeInputRef}
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleFileChange}
                    required
                  />
                  <div className="form-text">
                    {t("career_form_resume_hint")}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label career-checkbox">
                    <input
                      type="checkbox"
                      checked={formValues.gdprConsent}
                      onChange={(event) =>
                        setFormValues((current) => ({
                          ...current,
                          gdprConsent: event.target.checked,
                        }))
                      }
                      required
                    />
                    {t("career_form_gdpr_consent")}
                  </label>
                </div>
                {formStatus.error && (
                  <div className="alert alert-danger" role="alert">
                    {formStatus.error}
                  </div>
                )}
                {formStatus.success && (
                  <div className="alert alert-success" role="alert">
                    {formStatus.success}
                  </div>
                )}
                <button
                  type="submit"
                  className="theme-btn"
                  disabled={
                    formStatus.loading ||
                    !formValues.firstName.trim() ||
                    !formValues.lastName.trim() ||
                    !formValues.email.trim() ||
                    !formValues.phone.trim() ||
                    !formValues.address.trim() ||
                    !formValues.earliestStartDate ||
                    !formValues.germanLanguageLevel ||
                    !formValues.gdprConsent ||
                    !formValues.resume
                  }
                >
                  {formStatus.loading
                    ? t("career_apply_sending")
                    : t("career_apply_button")}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CareerSection;
